import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

function TextEntryDialog({
  open,
  onClose,
  onDone,
  initialText = "",
  submitOnEnter = false,
  placeholderText = "Type your text here...",
  showCharacterCount = true,
}) {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [text, setText] = useState(initialText);
  const MAX_CHARACTERS = 500; // Character limit for my_text fields

  useEffect(() => {
    if (open) {
      setText(initialText);
    }
  }, [open, initialText]);

  const handleDone = () => {
    if (text.trim()) {
      onDone(text);
      setText("");
    }
  };

  const handleCancel = () => {
    setText("");
    onClose();
  };

  const handleKeyDown = (e) => {
    if (
      e.key === "Enter" &&
      (submitOnEnter ? !e.shiftKey : e.ctrlKey || e.metaKey)
    ) {
      e.preventDefault();
      handleDone();
    }
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown={true}
      onClose={(event, reason) => {
        // Prevent closing by clicking outside or pressing escape
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        handleCancel();
      }}
      maxWidth="xs"
      fullScreen={isMobileOrTablet}
      slotProps={{
        paper: {
          sx: {
            zIndex: 20000,
            borderRadius: isMobileOrTablet ? 0 : 2,
            minWidth: "400px",
          },
        },
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 19999,
          },
        },
      }}
      sx={{
        zIndex: 20000,
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          pb: 1,
        }}
      >
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontWeight: 600,
            fontSize: "1rem",
          }}
        >
          Enter Text
        </Typography>
        {isMobile ? (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Button
              onClick={handleCancel}
              size="small"
              variant="outlined"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                minWidth: 76,
                borderRadius: 1,
                borderColor: "rgba(0, 0, 0, 0.23)",
                color: "rgba(0, 0, 0, 0.87)",
                "&:hover": {
                  borderColor: "rgba(0, 0, 0, 0.4)",
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDone}
              size="small"
              variant="contained"
              disabled={!text.trim()}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                minWidth: 76,
                borderRadius: 1,
                backgroundColor: "rgb(0, 145, 255)",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "rgb(0, 120, 215)",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: "rgba(0, 0, 0, 0.12)",
                  color: "rgba(0, 0, 0, 0.26)",
                },
              }}
            >
              Done
            </Button>
          </Box>
        ) : (
          <IconButton
            onClick={handleCancel}
            size="small"
            sx={{
              color: "rgba(0, 0, 0, 0.54)",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ padding: "16px", paddingBottom: "8px" }}>
        <TextField
          variant="outlined"
          fullWidth
          value={text}
          onChange={(e) => {
            const newValue = e.target.value;
            if (newValue.length <= MAX_CHARACTERS) {
              setText(newValue);
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholderText}
          autoFocus
          inputProps={{
            maxLength: MAX_CHARACTERS,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              "&:hover fieldset": {
                borderColor: "rgb(0, 145, 255)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "rgb(0, 145, 255)",
              },
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "rgb(0, 145, 255)",
            },
          }}
          InputProps={{
            sx: { borderRadius: 1 },
          }}
        />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              color: "rgba(0, 0, 0, 0.54)",
              fontSize: "0.7rem",
            }}
          >
            {submitOnEnter ? "Enter to submit" : "Ctrl+Enter to submit"}
          </Typography>
          {showCharacterCount ? (
            <Typography
              variant="caption"
              sx={{
                color:
                  text.length >= MAX_CHARACTERS
                    ? "rgb(211, 47, 47)"
                    : "rgba(0, 0, 0, 0.54)",
                fontSize: "0.7rem",
                fontWeight: text.length >= MAX_CHARACTERS ? 600 : 400,
              }}
            >
              {text.length}/{MAX_CHARACTERS}
            </Typography>
          ) : null}
        </Box>
      </DialogContent>

      {!isMobile && (
        <DialogActions
          sx={{
            padding: "8px 16px 16px 16px",
            gap: 1,
          }}
        >
          <Button
            onClick={handleCancel}
            size="small"
            sx={{
              color: "rgba(0, 0, 0, 0.87)",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
              fontWeight: 500,
              textTransform: "none",
              minWidth: "80px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDone}
            variant="contained"
            size="small"
            disabled={!text.trim()}
            sx={{
              backgroundColor: "rgb(0, 145, 255)",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "rgb(0, 120, 215)",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                backgroundColor: "rgba(0, 0, 0, 0.12)",
                color: "rgba(0, 0, 0, 0.26)",
              },
              fontWeight: 500,
              textTransform: "none",
              minWidth: "80px",
            }}
          >
            Done
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

export default TextEntryDialog;

